import { createScopedLogger, deepEqual, useErrorHandler } from "@@common";
import { IExpression, IExpressionEvaluatorContext, IVariableScope, ObjectVariableScope, VariableScopeObserver } from "@@expressions";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IsDesignMode } from "../Utils/runtimeDetection";
import { useObservedExpressionResult } from "./useObservedExpressionResult";

const NULL_RESULT: useObservedExpressionResult<any> = [undefined, false, () => Promise.resolve(undefined), []];

export default function useObservedExpression<T = any>(
  expression: IExpression | undefined,
  context: IExpressionEvaluatorContext,
  loggerName: string,
  valueTransformer?: (value: any, error: any) => T
): useObservedExpressionResult<T> {
  // In runtime mode, short-circuit when no expression
  // In design mode, always run hooks because expression could be added dynamically
  if (!IsDesignMode && !expression) {
    return NULL_RESULT;
  }

  // Note: These hooks are placed in this condition on purpoise to avoid unnecessary evaluation
  // The component definition WILL NOT change during the lifecycle of the component, so it's safe to call hooks conditionally based on its properties

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [value, setValue] = useState<T | undefined>();
  // Start as pending when an expression is present so consumers (e.g. ViewBodyComponent) do not
  // render children before the first evaluation completes. This prevents child components like
  // TableComponent from calling handleRefresh before OnOpen has populated required variables.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [isPending, setIsPending] = useState(!!expression);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const errorHandler = useErrorHandler();
  const logger = createScopedLogger(loggerName);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const observedPaths = useMemo(() => {
    if (!expression) {
      return [];
    }

    const observedVariables = context.Evaluator.collectReferencedVariables(expression);
    return observedVariables.map((varExpression) => varExpression.Value.join("."));
  }, [context.Evaluator, expression]);

  // Store the latest evaluateValue in a ref so observer callbacks always use current version
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const evaluateValueRef = useRef<((silentRefresh?: boolean) => Promise<T | undefined>) | null>(null);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const evaluateValue = useCallback(
    async (silentRefresh?: boolean) => {
      if (!expression) {
        return;
      }

      if (!silentRefresh) {
        setIsPending(true);
      }
      try {
        const evaluatedValue = await context.Evaluator.evaluate(expression, context);

        const transformedValue = valueTransformer ? valueTransformer(evaluatedValue, null) : evaluatedValue;
        if (silentRefresh) {
          // Observer guarantees a relevant variable changed. The underlying object may have been
          // mutated in-place, so prev === transformedValue (same reference). deepEqual would
          // short-circuit to true and suppress the state update. Force a new reference so React
          // re-renders consumers that depend on this value.
          setValue(
            transformedValue != null && typeof transformedValue === "object"
              ? Array.isArray(transformedValue)
                ? [...transformedValue]
                : { ...transformedValue }
              : transformedValue
          );
        } else {
          // Non-observer evaluation (mount, explicit refresh): use deepEqual to avoid
          // unnecessary re-renders when the value hasn't materially changed.
          setValue((prev) => (deepEqual(prev, transformedValue) ? prev : transformedValue));
        }

        return transformedValue;
      } catch (error) {
        errorHandler(error);

        logger.error("Error evaluating expression:", error);

        const transformedValue = valueTransformer ? valueTransformer(undefined, error) : undefined;
        setValue((prev) => (deepEqual(prev, transformedValue) ? prev : transformedValue));

        return transformedValue;
      } finally {
        if (!silentRefresh) {
          setIsPending(false);
        }
      }
    },
    // context.Id intentionally NOT in deps - versionId changes should flow through the observer
    // pattern, not cause useCallback recreation. context.Evaluator covers evaluator replacement.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [expression, context.Evaluator, setValue, errorHandler, loggerName, valueTransformer]
  );

  // Update ref with latest evaluateValue
  evaluateValueRef.current = evaluateValue;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    evaluateValue();

    if (observedPaths.length === 0) {
      // No dynamic paths to observe, nothing to do
      logger.debug("No observed paths for expression");
      return;
    }

    logger.debug("Observed paths for expression:", observedPaths);

    const handleVariableScopeChanged: VariableScopeObserver = (target: IVariableScope, name: string | null, value: any, prevValue: any) => {
      if (
        name !== null &&
        !observedPaths.some((observedPath) => {
          const lowerObserved = observedPath.toLowerCase();
          const lowerChanged = name.toLowerCase();

          // Exact match
          if (lowerObserved === lowerChanged) return true;

          // Check if observed path is a child of changed path (e.g., observing $item.contact, $item changed)
          // The observed path should start with changed path followed by a dot
          if (lowerObserved.startsWith(lowerChanged + ".")) return true;

          // Check if changed path is a child of observed path (e.g., observing $item, $item.contact changed)
          // The changed path should start with observed path followed by a dot
          if (lowerChanged.startsWith(lowerObserved + ".")) return true;

          return false;
        })
      ) {
        return;
      }

      evaluateValueRef.current?.(true);
    };

    // Register observer to update value on variable scope changes.
    // ObjectVariableScope automatically fans notifications down to child scopes,
    // so subscribing only to context.VariableScope is sufficient — changes to variables
    // in any ancestor scope (e.g. $view in a parent view context) will propagate here.
    const objectVariableScope = context.VariableScope as ObjectVariableScope;
    logger.debug("Adding observer to variable scope.");
    objectVariableScope.addObserver(handleVariableScopeChanged);

    return () => {
      logger.debug("Removing observer from variable scope.");
      objectVariableScope.removeObserver(handleVariableScopeChanged);
    };
    // context.Id intentionally NOT in deps - versionId increments on every set() call which would
    // retrigger the useEffect and call evaluateValue() again, causing an infinite loop.
    // Variable changes flow through the observer; scope replacement is detected by context.VariableScope.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.Evaluator, context.VariableScope, expression, loggerName, observedPaths]);

  return [value, isPending, evaluateValue, observedPaths];
}

export function useObservedExpressionObject<T = any>(
  expression: IExpression | undefined,
  context: IExpressionEvaluatorContext,
  loggerName: string,
  valueTransformer?: (value: any, error: any) => T
) {
  const [value, pending, refresh, observedPaths] = useObservedExpression<T>(expression, context, loggerName, valueTransformer);

  return {
    value,
    pending,
    refresh,
    observedPaths,
  };
}
